import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell, TableHead, TableRow,
  Chip, Pagination, TextField, InputAdornment, MenuItem, CircularProgress,
} from '@mui/material';
import { Search } from '@mui/icons-material';
import { useState, useEffect, useCallback } from 'react';
import api from '../../api/axios';

const actionColors = {
  tenant_created: 'success', tenant_updated: 'info', tenant_deleted: 'error',
  tenant_active: 'success', tenant_suspended: 'error', plan_changed: 'warning',
  invoice_created: 'info', invoice_paid: 'success', impersonated: 'warning',
  settings_updated: 'default', branding_updated: 'default',
};

export default function AuditLogsPage() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [action, setAction] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/superadmin/audit', {
        params: { page, limit: 50, search: search || undefined, action: action || undefined, from: from || undefined, to: to || undefined }
      });
      setLogs(data.data);
      setTotal(data.pagination?.total || 0);
    } catch(e) { console.error(e); }
    setLoading(false);
  }, [page, search, action, from, to]);

  useEffect(() => { load(); }, [load]);

  const actions = ['tenant_created','tenant_updated','tenant_deleted','tenant_active','tenant_suspended','plan_changed','invoice_created','invoice_paid','impersonated','settings_updated','branding_updated','domain_added'];

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} mb={1}>Audit Logs</Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>Complete audit trail of all admin actions</Typography>

      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField placeholder="Search description, user..." size="small" value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
              sx={{ width: 280 }} />
            <TextField select size="small" label="Action" value={action} onChange={e => { setAction(e.target.value); setPage(1); }} sx={{ width: 180 }}>
              <MenuItem value="">All Actions</MenuItem>
              {actions.map(a => <MenuItem key={a} value={a}>{a.replace(/_/g,' ')}</MenuItem>)}
            </TextField>
            <TextField size="small" label="From" type="date" value={from} onChange={e => { setFrom(e.target.value); setPage(1); }} InputLabelProps={{ shrink: true }} />
            <TextField size="small" label="To" type="date" value={to} onChange={e => { setTo(e.target.value); setPage(1); }} InputLabelProps={{ shrink: true }} />
          </Box>

          {loading ? <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box> : (
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell>Action</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Tenant</TableCell>
                  <TableCell>User</TableCell>
                  <TableCell>IP</TableCell>
                  <TableCell>Time</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logs.map(log => (
                  <TableRow key={log.id} hover>
                    <TableCell>
                      <Chip label={log.action.replace(/_/g,' ')} size="small" color={actionColors[log.action] || 'default'} />
                    </TableCell>
                    <TableCell><Typography variant="body2">{log.description}</Typography></TableCell>
                    <TableCell><Typography variant="body2">{log.tenant_name || '—'}</Typography></TableCell>
                    <TableCell>
                      <Typography variant="body2">{log.user_name}</Typography>
                      <Typography variant="caption" color="text.secondary">{log.user_role}</Typography>
                    </TableCell>
                    <TableCell><Typography variant="caption">{log.ip_address || '—'}</Typography></TableCell>
                    <TableCell><Typography variant="caption">{new Date(log.created_at).toLocaleString('en-PK', { month:'short', day:'2-digit', hour:'2-digit', minute:'2-digit' })}</Typography></TableCell>
                  </TableRow>
                ))}
                {logs.length === 0 && <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4 }}>No logs found</TableCell></TableRow>}
              </TableBody>
            </Table>
          )}

          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="caption" color="text.secondary">{total} total entries</Typography>
            <Pagination count={Math.ceil(total / 50)} page={page} onChange={(_, v) => setPage(v)} color="primary" />
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
